import { useState } from 'react'
import { Plus } from 'lucide-react'
import type { ZebraPrinter } from '@/api/labelPrintingApi'
import { useLabelPrintingOptions, useZebraPrinters, useZebraTemplates } from '@/hooks/useLabelPrinting'
import {
  allowedTemplatesText,
  locationSuggestions,
  printerStatus,
  sizeAndResolution,
  zebraConnectionText,
} from '@/lib/labelPrinting'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import type { SortAccessors } from '@/lib/tableSort'
import { Button } from '@/components/ui/button'
import { TableCell } from '@/components/ui/table'
import { ZebraPrinterDialog } from './ZebraPrinterDialog'
import { ListSection, ListTable, NameCell, StatusChip } from './shared'

const TABLE_ID = 'label-printing-zebra-printers'

const COLUMNS: TableColumnDef[] = [
  { id: 'displayName', header: 'Display name', locked: true, defaultWidth: 230, sortable: true },
  { id: 'host', header: 'Host', defaultWidth: 220, sortable: true },
  { id: 'connection', header: 'Connection', defaultWidth: 200, sortable: true },
  { id: 'sizeResolution', header: 'Size and resolution', defaultWidth: 190, sortable: true },
  { id: 'allowed', header: 'Allowed templates', defaultWidth: 260 },
  { id: 'status', header: 'Status', defaultWidth: 190, sortable: true },
]

const hostText = (p: ZebraPrinter) => `${p.host}:${p.port}`

const SORT: SortAccessors<ZebraPrinter> = {
  displayName: p => p.displayName,
  host: hostText,
  connection: zebraConnectionText,
  sizeResolution: p => sizeAndResolution(p.loadedStock, p.resolution),
  status: p => printerStatus(p).label,
}

/**
 * Zebra printers (z8nrz7evkc): network label printers, reached directly or
 * through a print server. No test print in this ticket.
 */
export function ZebraPrintersList({
  canManage,
  onNotice,
}: {
  canManage: boolean
  onNotice: (message: string) => void
}) {
  const query = useZebraPrinters()
  const templates = useZebraTemplates()
  const options = useLabelPrintingOptions()
  const [open, setOpen] = useState<{ id: string | null } | null>(null)
  const editing = open?.id ? query.data?.find(p => p.id === open.id) : undefined

  return (
    <>
      <ListSection
        title='Zebra Printers'
        blurb='One row per network label printer, reached directly or through a print server inside the site.'
        aside={
          canManage && (
            <Button onClick={() => setOpen({ id: null })}>
              <Plus className='mr-2 h-4 w-4' />
              Add printer
            </Button>
          )
        }
        footnote='These printers do not ask for a password. Keep them on the site’s private network.'
        query={query}
        noun='printers'
      >
        {rows => (
          <ListTable
            tableId={TABLE_ID}
            columns={COLUMNS}
            sort={SORT}
            rows={rows}
            cells={p => ({
              displayName: (
                <TableCell>
                  <NameCell name={p.displayName} onOpen={canManage ? () => setOpen({ id: p.id }) : undefined} />
                </TableCell>
              ),
              host: <TableCell className='font-mono text-xs'>{hostText(p)}</TableCell>,
              connection: <TableCell>{zebraConnectionText(p)}</TableCell>,
              sizeResolution: <TableCell>{sizeAndResolution(p.loadedStock, p.resolution)}</TableCell>,
              allowed: (
                <TableCell className='text-muted-foreground'>
                  {allowedTemplatesText(p.allowedTemplates) || '—'}
                </TableCell>
              ),
              status: (
                <TableCell>
                  <StatusChip {...printerStatus(p)} />
                </TableCell>
              ),
            })}
          />
        )}
      </ListSection>

      {open && (open.id === null || editing) && (
        <ZebraPrinterDialog
          key={open.id ?? 'new'}
          printer={editing}
          templates={templates.data ?? []}
          locations={locationSuggestions(options.data?.locations ?? [], (query.data ?? []).map(p => p.location))}
          onClose={() => setOpen(null)}
          onDone={message => {
            setOpen(null)
            onNotice(message)
          }}
        />
      )}
    </>
  )
}
